// =====================================================
// 📁 QuotationProductCardItem.tsx
// =====================================================

import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import ProductPanelService from '../../../../../../services/product/ProductPanelService';

import {
  formatProductPrice,
  getStockLabel,
  PRODUCT_CARD_ANIMATION,
  SharedProductItemProps,
} from '../../../shared/QuotationProductShared';

import QuotationProductAddButton from '../../../shared/QuotationProductAddButton';

const QuotationProductCardItem = ({
  product,
  disabled = false,
  onAddProductClick,
}: SharedProductItemProps) => {
  // =====================================================
  // 🔹 Image
  // =====================================================

  const [imageUrl, setImageUrl] = useState('');

  // =====================================================
  // 🔹 Effects
  // =====================================================

  useEffect(() => {
    const loadImage = async () => {
      if (!product.imagePath) return;

      try {
        const url = await ProductPanelService.getProductImage(
          product.imagePath
        );

        if (url) {
          setImageUrl(url);
        }
      } catch (error) {
        console.error('❌ Error loading product image:', error);
      }
    };

    loadImage();
  }, [product.imagePath]);

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <motion.div
      layout
      {...PRODUCT_CARD_ANIMATION}
      className="
      flex
      h-full
      flex-col

      overflow-hidden

      rounded-2xl

      border
      border-stroke

      bg-white

      shadow-sm

      transition-all
      duration-200

      hover:-translate-y-1
      hover:border-primary/20
      hover:bg-white
      hover:shadow-lg

      dark:border-strokedark
      dark:bg-boxdark

      dark:hover:border-primary/30
      dark:hover:bg-boxdark-2
      dark:hover:shadow-2xl
    "
    >
      {/* ===================================================== */}
      {/* Image */}
      {/* ===================================================== */}

      <div
        className="
        bg-gray-1

        relative
        flex

        h-32
        items-center

        justify-center

        overflow-hidden

        dark:bg-meta-4

        sm:h-36
        lg:h-[150px]
      "
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className="
            h-full
            w-full
            object-cover
          "
          />
        ) : (
          <span
            className="
            text-sm
            font-medium

            text-bodydark2
          "
          >
            IMG
          </span>
        )}

        {/* Stock */}

        <div
          className={`
          absolute
          right-3
          top-3

          inline-flex
          items-center
          justify-center

          rounded-full

          px-3
          py-1

          text-[11px]
          font-semibold

          shadow-sm

          ${
            product.stockQuantity > 0
              ? `
                  border
                  border-success/20

                  bg-success/10

                  text-success
                `
              : `
                  border
                  border-danger/20

                  bg-danger/10

                  text-danger
                `
          }
        `}
        >
          {product.stockQuantity}
        </div>
      </div>

      {/* ===================================================== */}
      {/* Content */}
      {/* ===================================================== */}

      <div
        className="
        flex
        flex-1
        flex-col

        p-4
      "
      >
        {/* Código */}

        <span
          className="
          text-[11px]
          font-semibold

          uppercase
          tracking-wider

          text-primary
        "
        >
          {product.code}
        </span>

        {/* Nombre */}

        <h3
          className="
          mt-2

          line-clamp-2

          min-h-[44px]

          text-sm
          font-semibold

          text-black

          dark:text-white
        "
        >
          {product.name}
        </h3>

        {/* Precio */}

        <div
          className="
          mt-4

          text-xl
          font-bold

          text-primary

          dark:text-white
        "
        >
          {formatProductPrice(product.price)}
        </div>

        {/* Estado */}

        <div className="mt-2">
          <span
            className={`
            inline-flex
            items-center

            rounded-full

            px-2.5
            py-1

            text-xs
            font-medium

            ${
              product.stockQuantity > 0
                ? 'bg-success/10 text-success'
                : 'bg-danger/10 text-danger'
            }
          `}
          >
            {getStockLabel(product.stockQuantity)}
          </span>
        </div>

        {/* ===================================================== */}
        {/* Footer */}
        {/* ===================================================== */}

        <div
          className="
          mt-auto

          pt-5
        "
        >
          <QuotationProductAddButton
            fullWidth
            disabled={disabled}
            onClick={() => onAddProductClick?.(product)}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default QuotationProductCardItem;
